import { Box, Input, InputProps, Text } from '@chakra-ui/react'
import React, { useState } from 'react'

import {
  getNodeInputFieldStyles,
  nodeInputFieldStylesCompact,
} from './nodeInputStyles'
import { NodeLabel } from './NodeLabel'

export interface NodeInputProps extends Omit<InputProps, 'size'> {
  label: string
  required?: boolean
  error?: string
  compact?: boolean
  onMouseDown?: (e: React.MouseEvent<HTMLInputElement>) => void
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void
  /** Custom increment handler for number inputs (overrides default) */
  onIncrement?: () => void
  /** Custom decrement handler for number inputs (overrides default) */
  onDecrement?: () => void
}

/**
 * Standardized input component for switchboard nodes with floating label
 * Label appears on the border to save space
 * Automatically applies the 'nodrag' class to prevent React Flow drag events.
 */
export const NodeInput: React.FC<NodeInputProps> = ({
  label,
  required = false,
  error,
  compact = false,
  onMouseDown,
  onClick,
  onIncrement,
  onDecrement,
  placeholder,
  ...inputProps
}) => {
  const inputOnBlur = inputProps.onBlur
  const inputOnFocus = inputProps.onFocus
  const inputOnChange = inputProps.onChange
  const inputType = inputProps.type
  const inputValue = inputProps.value
  const inputStep = inputProps.step
  const inputMin = inputProps.min
  const inputMax = inputProps.max
  const inputOnWheel = inputProps.onWheel
  const inputCss = inputProps.css

  // Track if field has been focused to stop pulsing animation
  const [hasBeenFocused, setHasBeenFocused] = useState(false)
  const onBlurRef = React.useRef(inputOnBlur)

  React.useEffect(() => {
    onBlurRef.current = inputOnBlur
  }, [inputOnBlur])

  // Reset hasBeenFocused when error clears
  React.useEffect(() => {
    if (!error) {
      setHasBeenFocused(false)
    }
  }, [error])

  const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    onMouseDown?.(e)
    e.stopPropagation()
  }

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    onClick?.(e)
    e.stopPropagation()
    // Stop pulsing when clicked
    if (error && !hasBeenFocused) {
      setHasBeenFocused(true)
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    inputOnFocus?.(e)
    // Stop pulsing when focused
    if (error && !hasBeenFocused) {
      setHasBeenFocused(true)
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    inputOnBlur?.(e)
  }

  const commitNumberValue = React.useCallback((nextValue: string) => {
    // Many node hooks commit numeric values on blur (using local state + onBlur).
    // Trigger a deferred blur callback after spinner clicks so arrow/tick changes persist.
    if (typeof onBlurRef.current !== 'function') return

    // Intentionally lightweight synthetic blur event.
    // Current switchboard blur consumers only depend on target/currentTarget.value.
    const syntheticBlurEvent = {
      target: { value: nextValue },
      currentTarget: { value: nextValue },
      type: 'blur',
      preventDefault: () => {},
      stopPropagation: () => {},
      persist: () => {},
    } as unknown as React.FocusEvent<HTMLInputElement>

    if (typeof window === 'undefined') {
      onBlurRef.current(syntheticBlurEvent)
      return
    }

    window.setTimeout(() => {
      onBlurRef.current?.(syntheticBlurEvent)
    }, 0)
  }, [])

  // Handle increment/decrement for number inputs
  const handleIncrement = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      // Use custom handler if provided
      if (onIncrement) {
        onIncrement()
        return
      }

      // Default behavior: increment via onChange
      if (inputType === 'number' && inputOnChange) {
        const currentValue = parseFloat(String(inputValue || 0)) || 0
        const step = inputStep ? parseFloat(String(inputStep)) : 1
        const max = inputMax ? parseFloat(String(inputMax)) : undefined

        let newValue = currentValue + step

        // Apply max constraint
        if (max !== undefined && newValue > max) {
          newValue = max
        }

        // Trigger onChange
        const changeEvent = {
          target: { value: String(newValue) },
        } as React.ChangeEvent<HTMLInputElement>
        inputOnChange(changeEvent)
        commitNumberValue(String(newValue))
      }
    },
    [
      commitNumberValue,
      inputMax,
      inputOnChange,
      inputStep,
      inputType,
      inputValue,
      onIncrement,
    ],
  )

  const handleDecrement = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      // Use custom handler if provided
      if (onDecrement) {
        onDecrement()
        return
      }

      // Default behavior: decrement via onChange
      if (inputType === 'number' && inputOnChange) {
        const currentValue = parseFloat(String(inputValue || 0)) || 0
        const step = inputStep ? parseFloat(String(inputStep)) : 1
        const min = inputMin ? parseFloat(String(inputMin)) : undefined

        let newValue = currentValue - step

        // Apply min constraint
        if (min !== undefined && newValue < min) {
          newValue = min
        }

        // Trigger onChange
        const changeEvent = {
          target: { value: String(newValue) },
        } as React.ChangeEvent<HTMLInputElement>
        inputOnChange(changeEvent)
        commitNumberValue(String(newValue))
      }
    },
    [
      commitNumberValue,
      inputMin,
      inputOnChange,
      inputStep,
      inputType,
      inputValue,
      onDecrement,
    ],
  )

  const showLabel = true // Always show labels
  const isNumberInput = inputType === 'number'

  // Add pulsing animation for error state (until focused)
  const shouldPulse = error && !hasBeenFocused

  const fieldStyles = compact
    ? {
        ...nodeInputFieldStylesCompact,
        ...(error
          ? {
              borderColor: 'border.error',
              _focus: {
                borderColor: 'border.error',
                borderWidth: '1px',
                outline: 'none',
                boxShadow: 'none',
              },
            }
          : {}),
      }
    : getNodeInputFieldStyles(!!error)

  return (
    <Box position="relative" width="100%">
      <Input
        className="nodrag"
        {...inputProps}
        {...fieldStyles}
        placeholder={placeholder || label}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onWheel={
          isNumberInput
            ? (e) => {
                // Prevent number input from changing value on scroll
                e.currentTarget.blur()
              }
            : inputOnWheel
        }
        css={{
          ...(isNumberInput
            ? {
                paddingRight: '32px',
                // Completely remove native spinner arrows
                '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  margin: 0,
                  opacity: 0,
                  width: 0,
                  height: 0,
                  display: 'none',
                },
                // For Firefox - remove spinner completely
                MozAppearance: 'textfield',
              }
            : {}),
          ...(shouldPulse
            ? {
                '@keyframes pulse-border': {
                  '0%, 100%': {
                    borderColor: 'var(--chakra-colors-border-error)',
                    boxShadow:
                      '0 0 0 0 color-mix(in srgb, var(--chakra-colors-fg-error) 40%, transparent)',
                  },
                  '50%': {
                    borderColor: 'var(--chakra-colors-border-error)',
                    boxShadow:
                      '0 0 0 3px color-mix(in srgb, var(--chakra-colors-fg-error) 20%, transparent)',
                  },
                },
                animation: 'pulse-border 2s ease-in-out infinite',
              }
            : {}),
          ...(inputCss as Record<string, unknown>),
        }}
      />
      {/* Custom spinner buttons for number inputs */}
      {isNumberInput && (
        <Box
          position="absolute"
          right={1}
          top="50%"
          transform="translateY(-50%)"
          display="flex"
          flexDirection="column"
          gap={0}
          pointerEvents="none"
          height="100%"
          justifyContent="center"
          css={{
            '& button': {
              pointerEvents: 'auto',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '1px 4px',
              fontSize: '8px',
              lineHeight: 1,
              color: 'var(--chakra-colors-fg-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '50%',
              '&:hover': {
                color: 'var(--chakra-colors-fg-default)',
                backgroundColor: 'var(--chakra-colors-bg-subtle)',
              },
              '&:active': {
                backgroundColor: 'var(--chakra-colors-bg-muted)',
              },
            },
          }}
        >
          <button
            type="button"
            onClick={handleIncrement}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            aria-label={`Increase ${label}`}
          >
            ▲
          </button>
          <button
            type="button"
            onClick={handleDecrement}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            aria-label={`Decrease ${label}`}
          >
            ▼
          </button>
        </Box>
      )}
      {showLabel && <NodeLabel label={label} required={required} />}
      {error && (
        <Text fontSize="xs" color="fg.error" mt={1}>
          {error}
        </Text>
      )}
    </Box>
  )
}
